import React, { useEffect, useMemo, useRef, useState } from "react";
import ShippingAddressSection, {
  EMPTY_ADDRESS,
  hasErrors,
  useShippingForm,
  validateAddress,
} from "./ShippingAddressSection";
import { buildCreateInternationalPayload } from "../../api/embassyGoodConductAppointment";

const noopFetchAreas = () => {};

function egyptCountryLabel(locale) {
  return locale === "ar" ? "مصر" : "Egypt";
}

function ShippingReceiverStep({ values, setData, navigation, t }) {
  const locale = values.locale || "en";

  const [submitted, setSubmitted] = useState(false);
  const [orderError, setOrderError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const savedReceiver = values.shippingDetails?.receiver || null;
  const stepInstanceId = useRef(
    `rcv-${Date.now()}-${Math.random().toString(36).slice(2)}`
  ).current;

  const egyptCountry = useMemo(
    () => ({ code: "EG", name: egyptCountryLabel(locale) }),
    [locale]
  );

  /** Receiver is always Egypt — strip stale country/coords from saved receiver. */
  const receiverInitialData = useMemo(() => {
    if (!savedReceiver) {
      return {
        ...EMPTY_ADDRESS,
        mobileISOCode: "eg",
        countryCode: "EG",
        country: egyptCountry.name,
        coords: null,
      };
    }
    const {
      country: _c,
      countryCode: _cc,
      coords: _coords,
      ...savedRest
    } = savedReceiver;
    return {
      ...EMPTY_ADDRESS,
      ...savedRest,
      mobileISOCode: savedRest.mobileISOCode || "eg",
      countryCode: "EG",
      country: egyptCountry.name,
      coords: null,
    };
  }, [savedReceiver, egyptCountry.name]);

  const receiverHydrateKey = useMemo(
    () =>
      `${stepInstanceId}|${
        savedReceiver
          ? [
              savedReceiver.email,
              savedReceiver.cityCode,
              savedReceiver.street,
              savedReceiver.area,
            ].join("|")
          : "new"
      }`,
    [stepInstanceId, savedReceiver]
  );

  /** Keep persisted receiver aligned with Egypt when returning from review edit. */
  useEffect(() => {
    if (!savedReceiver) return;
    if (
      savedReceiver.countryCode === "EG" &&
      savedReceiver.country === egyptCountry.name
    ) {
      return;
    }
    setData({
      name: "shippingDetails",
      value: {
        ...(values.shippingDetails || {}),
        receiver: {
          ...savedReceiver,
          countryCode: "EG",
          country: egyptCountry.name,
          coords: null,
        },
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once per receiver step visit

  const {
    formData,
    errors,
    setErrors,
    areaLookup,
    handleChange,
    handlePhoneChange,
    handleFlagChange,
    handleCityChange,
    touchAll,
  } = useShippingForm({
    initialData: receiverInitialData,
    hydrateKey: receiverHydrateKey,
    cityLookup: [],
    fetchAreas: noopFetchAreas,
    validateOptions: { requireCoords: false },
  });

  useEffect(() => {
    if (
      formData.countryCode === "EG" &&
      formData.country === egyptCountry.name
    ) {
      return;
    }
    handleChange({ target: { name: "countryCode", value: "EG" } });
    handleChange({ target: { name: "country", value: egyptCountry.name } });
  }, [
    formData.countryCode,
    formData.country,
    egyptCountry.name,
    handleChange,
  ]);

  const handleProceed = async () => {
    setSubmitted(true);
    touchAll();
    setOrderError("");
    const errs = validateAddress(formData, { requireCoords: false });
    setErrors(errs);
    if (hasErrors(errs)) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const receiver = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobile: `+${formData.mobileDialCode} ${formData.mobile}`,
      mobileRaw: formData.mobile,
      mobileDialCode: formData.mobileDialCode,
      landline: formData.landline,
      companyName: formData.companyName,
      country: egyptCountryLabel(locale),
      countryCode: "EG",
      state: formData.state,
      city: formData.cityName,
      cityName: formData.cityName,
      cityCode: formData.cityCode,
      area: formData.area,
      street: formData.street,
      building: formData.building,
      apartmentNo: formData.apartmentNo,
      floor: formData.floor,
      zip: formData.zip,
      additionalAddress: formData.additionalAddress,
      coords: null,
    };

    setData({
      name: "shippingDetails",
      value: {
        ...(values.shippingDetails || {}),
        receiver,
      },
    });

    try {
      const payload = await buildCreateInternationalPayload(
        values,
        {
          ...formData,
          countryCode: "EG",
          country: egyptCountryLabel(locale),
        },
        {
          accessToken: values.accessToken,
          locale,
        }
      );
      setData({ name: "internationalOrderPayload", value: payload });
      navigation.go("shipping-review");
    } catch (err) {
      const msg =
        err && typeof err.message === "string" && err.message.trim()
          ? err.message.trim()
          : t("GC_APPOINTMENT_ERROR");
      setOrderError(msg);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="shipping-receiver-step mt-4 mb-5">
      <div className="row align-items-center mb-4 gc-page-header">
        <div className="col-auto">
          <img
            src="./images/user-icon.svg"
            alt=""
            className="gc-section-icon"
          />
        </div>
        <div className="col">
          <h3 className="mb-0 gc-page-title">{t("SHIPPING_DETAILS_TITLE")}</h3>
          <p className="text-muted small mb-0 mt-1">
            {t("SHIPPING_DETAILS_SUBTITLE")}
          </p>
        </div>
      </div>

      {submitted && hasErrors(errors) && (
        <div className="alert alert-danger mb-4" role="alert">
          {t("ENTER_REQUIRED_FIELDS")}
        </div>
      )}

      <div className="gc-section-card mb-4">
        <div className="gc-section-heading">
          <img
            src="./images/user-icon.svg"
            alt=""
            className="gc-inline-icon"
          />
          <span>{t("SHIPPING_RECEIVER_TITLE")}</span>
        </div>

        <ShippingAddressSection
          formData={formData}
          errors={errors}
          cityLookup={[]}
          areaLookup={areaLookup}
          onChange={handleChange}
          onPhoneChange={handlePhoneChange}
          onFlagChange={handleFlagChange}
          onCityChange={(e) => handleCityChange(e, [])}
          hasCompanyName
          t={t}
          locale={locale}
          sectionKey="receiver"
          restrictToCountry="eg"
          showMap={false}
          fixedCountry={egyptCountry}
          embassyCitySearch={{
            accessToken: values.accessToken,
            countryCode: "EG",
          }}
        />
      </div>

      {orderError && (
        <div className="alert alert-danger mb-4" role="alert">
          {orderError}
        </div>
      )}

      <div className="row mt-5">
        <div className="col-6 dir-left">
          <button
            type="button"
            className="btn btn-lg btn-default btn-back"
            onClick={() => navigation.go("shipping-sender")}
          >
            <img src="./images/arrow-left.svg" alt="" />
            {t("BACK")}
          </button>
        </div>
        <div className="col-6 dir-right">
          <button
            type="button"
            className="btn btn-primary btn-lg emx-button"
            onClick={handleProceed}
          >
            {t("PROCEED")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShippingReceiverStep;
